// Client-facing services catalog for the Services (Service Discovery) module.
// Static config — the "AI recommended" set is derived at runtime from the
// client's projects. Icons map to lucide names in the page.

export interface ServiceQuestion {
  q: string;
  type: "yesno" | "text" | "select";
  options?: string[];
}
export interface CatalogService {
  key: string;
  name: string;
  category: string;
  icon: string;
  gradient: string;
  price: string;
  timeline: string;
  short: string;
  badge?: "Recommended" | "Popular" | "Best Seller" | "New";
  description: string;
  benefits: string[];
  deliverables: string[];
  tech: string[];
  industries: string[];
  faqs: { q: string; a: string }[];
  related: string[];
  questionnaire: ServiceQuestion[];
}

export const CATEGORIES = [
  "Recommended",
  "Website Development",
  "Mobile Apps",
  "Shopify",
  "WordPress",
  "Branding",
  "SEO",
  "Digital Marketing",
  "ERP Development",
  "AI Solutions",
  "Website Maintenance",
  "Hosting & Support",
];

export const SERVICES: CatalogService[] = [
  {
    key: "web-dev", name: "Website Development", category: "Website Development", icon: "Globe",
    gradient: "from-blue-500 to-indigo-600", price: "$2,500+", timeline: "4–6 Weeks", badge: "Popular",
    short: "Modern, responsive websites built for performance and conversions.",
    description: "Custom-designed, responsive websites engineered for speed, SEO and conversions — from marketing sites to complex web apps.",
    benefits: ["Higher conversions", "Fast, SEO-ready pages", "Fully responsive", "Easy to update"],
    deliverables: ["UI/UX Design", "Responsive Development", "CMS Integration", "Contact Forms", "Basic SEO Configuration", "Analytics Integration", "Performance Optimization"],
    tech: ["React", "Next.js", "Node.js", "Tailwind"],
    industries: ["SaaS", "Agencies", "E-commerce", "Startups"],
    faqs: [
      { q: "How long does a typical website take?", a: "Most marketing sites take 4–6 weeks; larger web apps 8–12 weeks." },
      { q: "Can I edit content myself?", a: "Yes — we integrate a CMS so your team can update content without code." },
    ],
    related: ["seo", "maintenance", "ai-chatbot", "hosting"],
    questionnaire: [
      { q: "Do you need a CMS?", type: "yesno" },
      { q: "E-commerce functionality?", type: "yesno" },
      { q: "Booking system?", type: "yesno" },
      { q: "Multi-language support?", type: "yesno" },
      { q: "Approximate number of pages?", type: "select", options: ["1–5", "6–15", "16–30", "30+"] },
      { q: "Blog required?", type: "yesno" },
    ],
  },
  {
    key: "mobile-app", name: "Cross-Platform App", category: "Mobile Apps", icon: "Smartphone",
    gradient: "from-violet-500 to-purple-600", price: "$5,000+", timeline: "8–12 Weeks",
    short: "Build once, deploy everywhere with a unified iOS, Android & Web codebase.",
    description: "Native-quality mobile apps from a single codebase — fast to market, cheaper to maintain, consistent across platforms.",
    benefits: ["One codebase", "Native performance", "Faster time to market", "Lower maintenance"],
    deliverables: ["UX Wireframes", "App UI Design", "Cross-platform Build", "API Integration", "App Store Submission", "QA Testing"],
    tech: ["React Native", "Flutter", "Node.js", "AWS"],
    industries: ["Fintech", "Health", "Retail", "Logistics"],
    faqs: [
      { q: "iOS and Android both?", a: "Yes — a single codebase ships to both stores." },
      { q: "Do you handle store submission?", a: "We prepare and submit to the App Store and Google Play." },
    ],
    related: ["ui-ux", "ai-chatbot", "maintenance"],
    questionnaire: [
      { q: "Android application?", type: "yesno" },
      { q: "iOS application?", type: "yesno" },
      { q: "User authentication?", type: "yesno" },
      { q: "Payment gateway integration?", type: "yesno" },
      { q: "Push notifications?", type: "yesno" },
      { q: "Admin dashboard?", type: "yesno" },
      { q: "Offline support?", type: "yesno" },
    ],
  },
  {
    key: "ai-chatbot", name: "Custom AI Chatbot", category: "AI Solutions", icon: "Bot",
    gradient: "from-cyan-500 to-blue-600", price: "$1,200+", timeline: "2 Weeks", badge: "Recommended",
    short: "Scale support with an AI agent trained on your brand voice and data.",
    description: "A custom AI assistant trained on your content and project data to handle support, sales and internal Q&A around the clock.",
    benefits: ["24/7 instant answers", "Lower support cost", "Trained on your data", "Escalates to humans"],
    deliverables: ["Knowledge Base Setup", "Custom Prompt Design", "Website Widget", "Analytics Dashboard"],
    tech: ["OpenAI", "LangChain", "Pinecone", "Node.js"],
    industries: ["SaaS", "E-commerce", "Support teams"],
    faqs: [
      { q: "What data can it use?", a: "Docs, FAQs, past tickets, product data — anything you provide." },
      { q: "Can it hand off to a person?", a: "Yes, it escalates complex queries to your team." },
    ],
    related: ["web-dev", "seo"],
    questionnaire: [
      { q: "Integrate with your website?", type: "yesno" },
      { q: "Train on existing documents?", type: "yesno" },
      { q: "Multi-language support?", type: "yesno" },
      { q: "Human handoff required?", type: "yesno" },
    ],
  },
  {
    key: "seo", name: "Advanced SEO Audit", category: "SEO", icon: "TrendingUp",
    gradient: "from-emerald-500 to-teal-600", price: "$800+", timeline: "1 Week", badge: "Recommended",
    short: "Deep technical analysis to improve search visibility & organic traffic.",
    description: "A technical + content SEO audit with a prioritised roadmap to grow organic traffic and rankings.",
    benefits: ["More organic traffic", "Better rankings", "Actionable roadmap", "Competitor insights"],
    deliverables: ["Technical Audit", "Keyword Research", "On-page Recommendations", "Backlink Analysis", "Prioritised Roadmap"],
    tech: ["Ahrefs", "GA4", "Search Console", "Screaming Frog"],
    industries: ["All industries"],
    faqs: [
      { q: "Do you implement the fixes?", a: "The audit includes a roadmap; implementation can be added as a retainer." },
    ],
    related: ["digital-marketing", "web-dev", "maintenance"],
    questionnaire: [
      { q: "Existing website to audit?", type: "yesno" },
      { q: "Target specific keywords?", type: "yesno" },
      { q: "International/multi-region SEO?", type: "yesno" },
    ],
  },
  {
    key: "digital-marketing", name: "Email Automation", category: "Digital Marketing", icon: "Mail",
    gradient: "from-orange-500 to-red-500", price: "$1,500+", timeline: "3 Weeks",
    short: "Personalized journeys and automated workflows that drive repeat sales.",
    description: "Lifecycle email marketing — automated flows, segmentation and campaigns that convert and retain customers.",
    benefits: ["Repeat conversions", "Automated flows", "Segmentation", "Clear reporting"],
    deliverables: ["Flow Strategy", "Template Design", "Automation Setup", "Segmentation", "Reporting Dashboard"],
    tech: ["HubSpot", "Klaviyo", "Mailchimp"],
    industries: ["E-commerce", "SaaS", "Retail"],
    faqs: [{ q: "Which platform?", a: "We work with Klaviyo, HubSpot and Mailchimp based on your stack." }],
    related: ["seo", "web-dev"],
    questionnaire: [
      { q: "Existing email platform?", type: "yesno" },
      { q: "E-commerce store?", type: "yesno" },
      { q: "Need template design?", type: "yesno" },
    ],
  },
  {
    key: "maintenance", name: "Monthly Maintenance", category: "Website Maintenance", icon: "Wrench",
    gradient: "from-slate-500 to-slate-700", price: "$250/mo", timeline: "Ongoing", badge: "Best Seller",
    short: "Proactive updates, monitoring and priority support for your assets.",
    description: "Keep your site secure, fast and up to date with proactive maintenance and priority support.",
    benefits: ["Security updates", "Uptime monitoring", "Priority support", "Monthly reports"],
    deliverables: ["Security Updates", "Performance Monitoring", "Backups", "Priority Support", "Monthly Report"],
    tech: ["Monitoring", "Cloud", "CI/CD"],
    industries: ["All industries"],
    faqs: [{ q: "Is it a contract?", a: "Month-to-month; cancel anytime." }],
    related: ["hosting", "seo"],
    questionnaire: [
      { q: "WordPress site?", type: "yesno" },
      { q: "E-commerce store?", type: "yesno" },
      { q: "Need daily backups?", type: "yesno" },
    ],
  },
  {
    key: "branding", name: "Brand Identity", category: "Branding", icon: "Palette",
    gradient: "from-pink-500 to-rose-600", price: "$1,800+", timeline: "3–4 Weeks",
    short: "A distinctive visual identity — logo, palette and brand guidelines.",
    description: "Complete brand identity: logo suite, colour, typography and guidelines that make you instantly recognisable.",
    benefits: ["Memorable identity", "Consistent assets", "Brand guidelines", "Print + digital ready"],
    deliverables: ["Logo Suite", "Colour & Typography", "Brand Guidelines", "Social Templates", "Stationery"],
    tech: ["Figma", "Adobe CC"],
    industries: ["Startups", "Agencies", "Retail"],
    faqs: [{ q: "How many logo concepts?", a: "Typically 3 concepts with 2 revision rounds." }],
    related: ["web-dev", "ui-ux"],
    questionnaire: [
      { q: "Do you have an existing logo?", type: "yesno" },
      { q: "Need social media templates?", type: "yesno" },
      { q: "Print materials required?", type: "yesno" },
    ],
  },
  {
    key: "shopify", name: "Shopify Store", category: "Shopify", icon: "ShoppingBag",
    gradient: "from-lime-500 to-green-600", price: "$3,000+", timeline: "4–6 Weeks",
    short: "High-converting Shopify stores with custom theme & app setup.",
    description: "Custom Shopify storefronts built to convert — theme customization, apps, payments and launch.",
    benefits: ["Higher conversion", "Custom theme", "App integrations", "Launch support"],
    deliverables: ["Theme Customization", "Product Setup", "Payment & Shipping", "App Integrations", "Launch Support"],
    tech: ["Shopify", "Liquid", "React"],
    industries: ["Retail", "DTC", "Fashion"],
    faqs: [{ q: "Migrate from another platform?", a: "Yes — we migrate products, customers and orders." }],
    related: ["seo", "digital-marketing", "maintenance"],
    questionnaire: [
      { q: "Migrating an existing store?", type: "yesno" },
      { q: "Custom theme needed?", type: "yesno" },
      { q: "Subscriptions/recurring billing?", type: "yesno" },
    ],
  },
  {
    key: "erp", name: "Custom ERP", category: "ERP Development", icon: "Boxes",
    gradient: "from-amber-500 to-orange-600", price: "$12,000+", timeline: "12–16 Weeks",
    short: "Streamline operations with a tailored ERP built around your workflow.",
    description: "Custom ERP systems — inventory, HR, finance and operations unified into one platform designed around how you work.",
    benefits: ["Unified operations", "Custom workflows", "Role-based access", "Scalable"],
    deliverables: ["Requirements Analysis", "Modules (Inventory/HR/Finance)", "Role-based Access", "Reporting", "Training"],
    tech: ["Laravel", "MySQL", "React", "Docker"],
    industries: ["Manufacturing", "Distribution", "Services"],
    faqs: [{ q: "Can it integrate with our tools?", a: "Yes — via APIs to your existing systems." }],
    related: ["ai-chatbot", "hosting"],
    questionnaire: [
      { q: "Inventory module?", type: "yesno" },
      { q: "HR & payroll module?", type: "yesno" },
      { q: "Finance & accounting?", type: "yesno" },
      { q: "Multi-branch support?", type: "yesno" },
      { q: "Third-party integrations?", type: "yesno" },
    ],
  },
  {
    key: "hosting", name: "Managed Hosting", category: "Hosting & Support", icon: "Server",
    gradient: "from-sky-500 to-blue-700", price: "$99/mo", timeline: "Ongoing",
    short: "Fast, secure, managed cloud hosting with 24/7 monitoring.",
    description: "Managed cloud hosting with monitoring, backups, SSL and scaling handled for you.",
    benefits: ["99.9% uptime", "Auto backups", "SSL & CDN", "24/7 monitoring"],
    deliverables: ["Cloud Setup", "SSL & CDN", "Daily Backups", "Monitoring", "Scaling"],
    tech: ["AWS", "Cloudflare", "Docker"],
    industries: ["All industries"],
    faqs: [{ q: "Can you migrate our site?", a: "Yes, migration is included in onboarding." }],
    related: ["maintenance", "web-dev"],
    questionnaire: [
      { q: "Migrating an existing site?", type: "yesno" },
      { q: "Need a staging environment?", type: "yesno" },
      { q: "High-traffic scaling?", type: "yesno" },
    ],
  },
  {
    key: "ui-ux", name: "UI/UX Design", category: "Branding", icon: "PenTool",
    gradient: "from-fuchsia-500 to-purple-600", price: "$2,000+", timeline: "3–5 Weeks", badge: "New",
    short: "Research-led product design that users love and that converts.",
    description: "End-to-end product design — research, wireframes, prototypes and polished UI ready for development.",
    benefits: ["User-centered", "Clickable prototypes", "Design system", "Dev-ready handoff"],
    deliverables: ["UX Research", "Wireframes", "Prototype", "UI Design", "Design System"],
    tech: ["Figma", "Maze", "Framer"],
    industries: ["SaaS", "Mobile", "Web apps"],
    faqs: [{ q: "Do you deliver a design system?", a: "Yes — components and tokens ready for engineering." }],
    related: ["web-dev", "mobile-app", "branding"],
    questionnaire: [
      { q: "Existing product to redesign?", type: "yesno" },
      { q: "Need user research?", type: "yesno" },
      { q: "Design system required?", type: "yesno" },
    ],
  },
];

export const serviceByKey = (key: string) => SERVICES.find((s) => s.key === key);
